/**
 * @fileoverview The controls above the garden: one row, and under it what is narrowing it.
 *
 * The row is search, one button for sort and filter, and the view switch,
 * the same height, at any width. It used to be search and Filters on one
 * line and a sort dropdown and the view switch wrapping onto a second, on a
 * phone, with the order taking a whole box for a setting chosen once. The
 * order now lives in the sheet, and a quiet line says so when it is not the
 * usual one.
 *
 * Everything narrowing the garden shows under the row as a chip that undoes
 * itself: a week line's plants (named by the line, "Too cold Fri–Tue
 * nights", unless the weather bar is naming it just above), a mark a Today
 * link opened ("Chilly Tue night"), a bed, a stage, the archive, a date
 * range. "6 of 9" says how much of the garden is showing.
 *
 * Where: PlantGrid, under the weather bar.
 * API: none; the week's lines come with the brief (PlantGrid).
 * Settings: the view settings it shows and changes (plantViews);
 * DEFAULT_VIEW_SETTINGS and CLEARED_FILTERS.
 * Words (en/es): plants.toolbar.* (searchShort, searchPlaceholder,
 * chosenPlants, publicOn, canPlantNow, removeFilter, clearAll, ofTotal, sortedBy, view,
 * viewCards, viewGrid, viewList) and the option names in
 * plantFilterOptions.
 *
 * @module PlantToolbar
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ViewSwitch } from '@/shared/components/ViewSwitch';
import { Input } from '@/shared/components/ui/input';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { PlantFilterSheet } from './PlantFilterSheet';
import { SORTS, STATUSES, DATE_RANGES } from './plantFilterOptions';
import { adviceIcon } from './card/Verdict';
import type { GardenMark } from '@/shared/api/plantsBrief';
import type { BriefWeekLine } from '@/shared/api/todayBrief';
import { Pill } from '@/shared/components/ui/Pill';
import { TOKEN_TEXT } from '@/shared/theme/verdictTokens';
import {
  CLEARED_FILTERS, DEFAULT_VIEW_SETTINGS,
  type PlantView, type PlantViewSettings,
} from '../lib/plantViews';

/** What the toolbar controls, and the garden it counts. */
interface PlantToolbarProps {
  /** The garden's current view settings. */
  settings: PlantViewSettings;
  /** Applies one changed setting; the page owns the whole object. */
  onChange: (patch: Partial<PlantViewSettings>) => void;
  /** The beds, for the sheet and for naming a bed chip. */
  locations: Array<{ id: number; name: string }>;
  /** The stages, for the sheet and for naming a stage chip. */
  stages: Array<{ id: number; name: string }>;
  /** How many plants survive the current filters. */
  shown: number;
  /** How many plants there are in all. */
  total: number;
  /** The garden's marks (its cards' chips gathered), for naming the chip of a mark a Today link filtered by. */
  marks: GardenMark[];
  /** The week's lines (the brief's), offered as filters in the sheet, in the weather bar's words. */
  lines: BriefWeekLine[];
  /**
   * Leave out the chip for a week line's plants: the weather bar's pressed
   * line names them and clears them just above. It still counts on the
   * filter button.
   */
  hidePlantsChip?: boolean;
}

/** One thing narrowing the garden, as a chip that undoes it. */
interface Chip {
  /** Which filter it is, for React. */
  key: string;
  /** Read out by the chip's button, which removes it. */
  name: string;
  /** A verdict's icon, for a verdict chip. */
  icon?: React.ReactNode;
  /** The settings that undo it. */
  clear: Partial<PlantViewSettings>;
  /** A mark chip's ink, from its token. */
  ink?: string;
}

/**
 * Search, sort and filter, and the view switch, with the chips under them.
 *
 * @param props - PlantToolbarProps.
 */
export const PlantToolbar: React.FC<PlantToolbarProps> = ({
  settings, onChange, locations, stages, shown, total, marks, lines, hidePlantsChip = false,
}) => {
  const { reactTranslate: t } = useTranslation();

  /** One chip per thing narrowing the garden, each undoing only itself. */
  const chips: Chip[] = [];
  if (settings.verdict) {
    // A mark a Today link opened (?verdict=), named as the cards say it.
    const mark = marks.find((m) => m.key === settings.verdict);
    if (mark) {
      const Icon = adviceIcon(mark.kind, mark.code);
      chips.push({
        key: 'verdict',
        name: mark.title,
        icon: <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
        ink: mark.token ? TOKEN_TEXT[mark.token] : undefined,
        clear: { verdict: '' },
      });
    }
  }
  if (settings.plantIds && !hidePlantsChip) {
    // The plants a week line or a Coming up rule's line is about, named by
    // that line, unless the weather bar is naming it already.
    chips.push({
      key: 'plants',
      name: settings.plantsFor || t('plants.toolbar.chosenPlants'),
      clear: { plantIds: '', plantsFor: '' },
    });
  }
  if (settings.status !== DEFAULT_VIEW_SETTINGS.status) {
    const status = STATUSES.find((o) => o.value === settings.status);
    chips.push({ key: 'status', name: status ? t(status.key) : settings.status, clear: { status: 'active' } });
  }
  if (settings.public) {
    chips.push({ key: 'public', name: t('plants.toolbar.publicOn'), clear: { public: false } });
  }
  if (settings.canPlantNow) {
    chips.push({ key: 'canPlantNow', name: t('plants.toolbar.canPlantNow'), clear: { canPlantNow: false } });
  }
  if (settings.location !== 'all') {
    const name = locations.find((l) => String(l.id) === settings.location)?.name ?? settings.location;
    chips.push({ key: 'location', name, clear: { location: 'all' } });
  }
  if (settings.stage !== 'all') {
    const name = stages.find((st) => String(st.id) === settings.stage)?.name ?? settings.stage;
    chips.push({ key: 'stage', name, clear: { stage: 'all' } });
  }
  if (settings.dateRange !== 'any') {
    const range = DATE_RANGES.find((o) => o.value === settings.dateRange);
    const custom = settings.dateRange === 'custom'
      ? [settings.dateFrom, settings.dateTo].filter(Boolean).join(' – ')
      : '';
    chips.push({
      key: 'date',
      name: custom || (range ? t(range.key) : settings.dateRange),
      clear: { dateRange: 'any', dateFrom: '', dateTo: '' },
    });
  }

  /** Whether anything is hidden, so "6 of 9" is worth saying. */
  const isFiltered = shown !== total;
  /** The current order's option, for the line that names an unusual one. */
  const sort = SORTS.find((o) => o.value === settings.sort);

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={settings.search}
            onChange={(event) => onChange({ search: event.target.value })}
            placeholder={t('plants.toolbar.searchShort')}
            aria-label={t('plants.toolbar.searchPlaceholder')}
            className="h-10 pl-8"
          />
        </div>

        <PlantFilterSheet
          settings={settings}
          onChange={onChange}
          locations={locations}
          stages={stages}
          activeCount={chips.length + (settings.plantIds && hidePlantsChip ? 1 : 0)}
          onClearAll={() => onChange(CLEARED_FILTERS)}
          shown={shown}
          lines={lines}
        />

        <ViewSwitch
          value={settings.view}
          onChange={(view: PlantView) => onChange({ view })}
          label={t('plants.toolbar.view')}
          names={{
            cards: t('plants.toolbar.viewCards'),
            grid: t('plants.toolbar.viewGrid'),
            list: t('plants.toolbar.viewList'),
          }}
        />
      </div>

      {(chips.length > 0 || isFiltered) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <Pill
              key={chip.key}
              tone="outline"
              onClick={() => onChange(chip.clear)}
              aria-label={t('plants.toolbar.removeFilter', { name: chip.name })}
              icon={chip.icon}
              trailing={<X className="opacity-70" />}
              className={cn('bg-background text-foreground', chip.ink)}
            >
              {chip.name}
            </Pill>
          ))}
          {chips.length > 1 && (
            <button
              type="button"
              onClick={() => onChange(CLEARED_FILTERS)}
              className="px-1 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t('plants.toolbar.clearAll')}
            </button>
          )}
          {isFiltered && (
            <span className="ml-auto text-xs text-muted-foreground tabular-nums" aria-live="polite">
              {t('plants.toolbar.ofTotal', { shown, total })}
            </span>
          )}
        </div>
      )}

      {/* The order is set in the sheet; out here only when it is not the usual one. */}
      {settings.sort !== DEFAULT_VIEW_SETTINGS.sort && sort && (
        <p className="text-xs text-muted-foreground">
          {t('plants.toolbar.sortedBy', { sort: t(sort.key).toLowerCase() })}
        </p>
      )}
    </div>
  );
};
